import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import '../styles/components/Toasts.css';

/**
 * Lightweight toast/transport-status provider.
 *
 * Previously every page hand-rolled its own error banners: some used inline
 * ``setError`` strings, some used ``window.alert``, some silently swallowed
 * failures into ``console.error``. That made common user-visible failures
 * (WS dropped, start rejected, join conflict) inconsistent at best and
 * invisible at worst.
 *
 * ``ToastContext`` centralises short-lived feedback:
 *
 *   - ``showToast({ message, variant })`` / ``showSuccess`` / ``showError`` /
 *     ``showInfo`` helpers for imperative calls from event handlers.
 *   - Optional ``id`` so callers can update a single ongoing status toast
 *     (e.g. transport-reconnecting) rather than spawning duplicates.
 *   - ``dismiss(id)`` for sticky toasts (``duration === 0``) that need to be
 *     cleared programmatically.
 *
 * Ported pages should replace ad-hoc banners with ``const toast = useToast()``
 * and ``toast.showError(mapApiError(err))``.
 */

const ToastContext = createContext(null);

const DEFAULT_DURATION_MS = 4500;

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Graceful no-op when the provider isn't mounted yet (e.g. during tests).
    return {
      showToast: () => {},
      showSuccess: () => {},
      showError: () => {},
      showInfo: () => {},
      showWarning: () => {},
      dismiss: () => {},
    };
  }
  return ctx;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    ({
      id: providedId,
      message,
      variant = 'info',
      duration = DEFAULT_DURATION_MS,
      description = null,
    }) => {
      if (!message) return null;
      const id = providedId || `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      setToasts((prev) => {
        const without = prev.filter((t) => t.id !== id);
        return [...without, { id, message, variant, description }];
      });
      const existingTimer = timersRef.current.get(id);
      if (existingTimer) window.clearTimeout(existingTimer);
      if (duration > 0) {
        const t = window.setTimeout(() => {
          dismiss(id);
        }, duration);
        timersRef.current.set(id, t);
      }
      return id;
    },
    [dismiss],
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current.clear();
    },
    [],
  );

  const value = useMemo(
    () => ({
      showToast,
      showSuccess: (message, opts = {}) =>
        showToast({ ...opts, message, variant: 'success' }),
      showError: (message, opts = {}) =>
        showToast({ ...opts, message, variant: 'error' }),
      showInfo: (message, opts = {}) =>
        showToast({ ...opts, message, variant: 'info' }),
      showWarning: (message, opts = {}) =>
        showToast({ ...opts, message, variant: 'warning' }),
      dismiss,
    }),
    [showToast, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="region" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.variant}`}
            role={t.variant === 'error' ? 'alert' : 'status'}
          >
            <div className="toast__body">
              <div className="toast__message">{t.message}</div>
              {t.description && (
                <div className="toast__description">{t.description}</div>
              )}
            </div>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContext;
